/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import UniversalTracker from '../model/UniversalTracker';
import { ValueData } from '../types/surveyScope';
import {
  Alternative,
  DataPeriods,
  TableColumnType,
  UniversalTrackerBlueprintMin,
  UniversalTrackerMin,
  UniversalTrackerPlain,
  UtrValueType,
} from '../types/universalTracker';
import { DATE, formatDate, subtractDate } from './date';

export const UtrValueTypes = {
  calculation: 'calculation',
  number: 'number',
  percentage: 'percentage',
  sample: 'sample',
  numericValueList: 'numericValueList',
  text: 'text',
  date: 'date',
  valueList: 'valueList',
  valueListMulti: 'valueListMulti',
  textValueList: 'textValueList',
  table: 'table',
}

export const UtrValueTypeMap = {
  [UtrValueType.Text]: 'Text',
  [UtrValueType.Date]: 'Date',
  [UtrValueType.ValueList]: 'List',
  [UtrValueType.ValueListMulti]: 'Multiple-choice List',
  [UtrValueType.NumericValueList]: 'Multiple-choice List (Numerical)',
  [UtrValueType.TextValueList]: 'List',
  [UtrValueType.Table]: 'Table',
  [UtrValueType.Number]: 'Numeric',
  [UtrValueType.Percentage]: 'Percentage',
  [UtrValueType.Sample]: 'Sample or Proportion',
};

export const primitiveNumberTypes = [
  UtrValueTypes.number,
  UtrValueTypes.percentage,
  UtrValueTypes.sample
];


export function isNumeric(utr: Pick<UniversalTrackerMin, 'valueType'>) {
  switch (utr.valueType) {
    case UtrValueTypes.number:
    case UtrValueTypes.percentage:
    case UtrValueTypes.sample:
    case UtrValueTypes.numericValueList:
      return true;
    case UtrValueTypes.text:
    case UtrValueTypes.date:
    case UtrValueTypes.valueList:
    case UtrValueTypes.valueListMulti:
    default:
      return false;
  }
}

export function canAddTarget(utr: Pick<UniversalTrackerPlain, 'valueType' | 'valueValidation'>) {
  switch (utr.valueType) {
    case UtrValueTypes.number:
    case UtrValueTypes.percentage:
    case UtrValueTypes.numericValueList:
      return true;
    case UtrValueTypes.table:
      if (utr.valueValidation?.table?.validation?.maxRows === 1) {
        return true;
      }
      return false;
    case UtrValueTypes.sample:
    case UtrValueTypes.text:
    case UtrValueTypes.date:
    case UtrValueTypes.valueList:
    case UtrValueTypes.valueListMulti:
    default:
      return false;
  }
}

export function isScalarValue(valueType?: string) {
  if (!valueType) {
    return false;
  }

  if (primitiveNumberTypes.includes(valueType)) {
    return true;
  }

  // Scalar value expected in valueData.data
  return [UtrValueTypes.text, UtrValueTypes.date, UtrValueTypes.valueList].includes(valueType)
}

export function getPeriodName(period: DataPeriods | undefined, asPeriodicity = true) {
  switch (period) {
    case DataPeriods.Monthly:
      return asPeriodicity ? 'Monthly' : 'Month';
    case DataPeriods.Quarterly:
      return 'Quarterly';
    case DataPeriods.Yearly:
    default:
      return asPeriodicity ? 'Annually' : 'Annual';
  }
}

export function getPeriodRange(period: DataPeriods | undefined, date: string) {
  switch (period) {
    case DataPeriods.Monthly:
      return {
        period,
        from: subtractDate(date, 1, 'months'),
        to: formatDate(date, DATE.MONTH_YEAR, true),
      };
    case DataPeriods.Quarterly:
      return {
        period,
        from: subtractDate(date, 3, 'months'),
        to: formatDate(date, DATE.MONTH_YEAR, true),
      };
    case DataPeriods.Yearly:
    default:
      return {
        period: DataPeriods.Yearly,
        from: subtractDate(date, 1, 'years'),
        to: formatDate(date, DATE.MONTH_YEAR, true),
      };
  }
}

export const isValueDataNumeric = (utr: Pick<UniversalTrackerMin, 'valueType'>) => utr.valueType === UtrValueTypes.numericValueList;

export const isCheckboxType = (type: string) => [
  UtrValueTypes.numericValueList,
  UtrValueTypes.textValueList,
].includes(type)

export function getTableConfiguration(utr: UniversalTracker, ignoreType = false) {
  const u = utr.getRaw() || utr;
  const checkTableType = !ignoreType && u.valueType !== 'table'
  if (checkTableType || !u.valueValidation) {
    return undefined;
  }
  return u.valueValidation.table;
}

// Handle conversion between textValueList/numericValueList to valueListMulti
const getValueDataMulti = (data: ValueData['data']) => {
  if (Array.isArray(data)) {
    return data;
  }

  if (typeof data === 'object') {
    return Object.keys(data);
  }
  return [];
};

interface ValueDataArrayProps {
  utr: UniversalTracker;
  data: ValueData['data'];
  unit: string;
  numberScale?: string;
  displayCheckbox?: {
    [key: string]: boolean;
  };
  value: number | string | undefined;
}

interface ValueDataArrayReturn {
  label?: string;
  value: string | number;
  unit: string;
  numberScale?: string;
}

export function getValueDataArray(props: ValueDataArrayProps): ValueDataArrayReturn[] {
  const { utr, data, unit, numberScale, displayCheckbox, value } = props;
  switch (utr.getValueType()) {
    case UtrValueTypes.valueList:
      return [{
        label: data,
        value: data ? utr.getValueListData(data) : 'N/A',
        unit,
        numberScale,
      }];
    case UtrValueTypes.valueListMulti:
      return getValueDataMulti(data).map((k) => ({
        label: k,
        value: utr.getValueListData(k),
        unit,
        numberScale,
      }));
    case UtrValueTypes.numericValueList:
      return utr.getValueListOptions().map((item: { code: string }) => {
        const k = item.code;
        return {
          label: utr.getValueListData(k),
          value: (!displayCheckbox || displayCheckbox[k]) && data && !isNaN(data[k]) ? data[k] : 'N/A',
          unit,
          numberScale,
        }
      });
    case UtrValueTypes.textValueList:
      return utr.getValueListOptions().map((item: { code: string }) => {
        const k = item.code;
        return {
          label: utr.getValueListData(k),
          value: (!displayCheckbox || displayCheckbox[k]) && data && data[k] ? data[k] : 'N/A',
          unit,
          numberScale,
        }
      });
    default:
      return [{
        value: convertToNumberOrString(value),
        unit,
        numberScale,
      }];
  }
}

const convertToNumberOrString = (value: undefined | string | number): number | string => {
  if (value === undefined || isNaN(value as any)) {
    return 'N/A';
  }
  return value;
}

export const getQuestionShortPrefix = (utr: UniversalTracker) => {
  const alternativeCode = utr.getType();
  return utr.getShortPrefix(alternativeCode);
}

export const getQuestionName = (utr: UniversalTracker) => {
  const alternativeCode = utr.getType();
  return utr.getName(alternativeCode);
}

function __hasAlternativeInfo(utr: UniversalTrackerPlain, alternativeCode = '') {
  if (!utr.alternatives) {
    return false;
  }
  return alternativeCode in utr.alternatives;
}

function __get(attribute: keyof UniversalTrackerPlain | keyof Alternative, utr: UniversalTrackerPlain, alternativeCodes?: string[]): unknown {
  let returnAttribute = utr[attribute as keyof UniversalTrackerPlain];

  if (!alternativeCodes) {
    return returnAttribute;
  }

  alternativeCodes.forEach(code => {
    if (code === utr.type) {
      return returnAttribute = utr[attribute as keyof UniversalTrackerPlain];
    }
    if (__hasAlternativeInfo(utr, code)) {
      return returnAttribute = utr.alternatives?.[code]?.[attribute as keyof Alternative];
    }
  });

  return returnAttribute;
}

export function getName(utr: UniversalTrackerPlain, alternativeCodes?: string[]): string { return __get('name', utr, alternativeCodes) as string; }

export const isNumericTableColumnType = (type: TableColumnType) => {
  return [TableColumnType.Number, TableColumnType.Percentage].includes(type);
};

export const isNumericValueType = (valueType: UtrValueType) => {
  return [UtrValueType.Number, UtrValueType.Percentage, UtrValueType.NumericValueList].includes(valueType);
};

export const hasNumericColumnType = ({ valueValidation }: Pick<UniversalTrackerPlain, 'valueValidation'>) => {
  const columns = valueValidation?.table?.columns;
  return columns?.some((column) => isNumericTableColumnType(column.type));
};

export const hasStandard = (utr: Pick<UniversalTrackerBlueprintMin, 'type' | 'alternatives'>, code: string) => {
  return utr.type === code || !!utr.alternatives?.[code];
};

export const hasFramework = (utr: Pick<UniversalTrackerBlueprintMin, 'tags'>, subTag: string) => {
  if (!utr.tags) {
    return false;
  }

  return Object.values(utr.tags).flat().includes(subTag);
};
